import './index.css';
import { ChangeEvent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showToast } from '../../actions/app-actions-types';
import { sendMobileOTP, verifyMobileOTP } from '../../actions/user-actions-types';
import useTheme from '../../utilities/hooks/theme';
import I18n from '../../utilities/locale';
import Constants from '../../constants';
import View from '../view';
import Text from '../text';
import Card from '../card';
import Button from '@mui/material/Button/Button';
import TextField from '@mui/material/TextField';
import React from 'react';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';
import Regex from '../../utilities/Regex';
import PhoneDetails from '../phone_details';

interface Props {
  list: any;
  onVerifySuccess: any;
}

const VerifyPhoneItem = ({ list, onVerifySuccess }: Props) => {
  const dispatch = useDispatch();
  const [expand, setExpand] = useState(false);
  const [code, setCode] = useState('');
  const [isNeedToVerify, setIsNeedToVerify] = useState(false);
  const { ComponentTheme: theme } = useTheme();
  const [item = {}] = list;
  const { phone = '', verified = false } = item;

  useEffect(() => {
    setIsNeedToVerify(!verified && phone.length > 0);
  }, [verified, phone]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (Regex.validateNumber(event.target.value) || event.target.value === '') {
      setCode(event.target.value);
    }
  };

  const onPressResendOTP = () => onPressSendOTP(true);

  const onPressSendOTP = (resendOTP = false) => {
    const data = {
      countryCode: '+91',
      phone,
      resendOTP
    };
    dispatch(
      sendMobileOTP({
        data,
        callback: ({ result, error }: { result: any; error: any }) => {
          if (!error && result != null) {
            if (result.status === 'ERROR') {
              dispatch(showToast(I18n.t(result.message)));
            } else {
              dispatch(showToast(I18n.t('MobileOTPSentMsg')));
            }
          }
        }
      })
    );
  };

  const onPressVerify = () => {
    const data = {
      phone,
      countryCode: '+91',
      otp: code
    };
    dispatch(
      verifyMobileOTP({
        data,
        callback: ({ result, error }: { result: any; error: any }) => {
          if (!error && result != null) {
            if (result.status === 'ERROR') {
              dispatch(showToast(I18n.t(result.status)));
            } else {
              dispatch(showToast(I18n.t('OTPVerifiedSuccess')));
              setExpand(false);
              onVerifySuccess();
            }
          }
        }
      })
    );
  };

  const container = {
    borderWidth: verified ? '1.5px' : 0,
    backgroundColor: theme.verifyAccountItemContainer,
    borderColor: theme.verifyAccountItemContainerBorder
  };

  const headerContainer = {
    backgroundColor: theme.verifyAccountItemHeaderContainer
  };
  const contentWrapper = {
    backgroundColor: theme.verifyAccountItemContent
  };
  const headerTitle = {
    color: theme.verifyAccountItemTitle
  };

  const headerVerified = {
    color: theme.verifyAccountItemVerified
  };

  const verifyBtn = {
    color: theme.verifyAccountItemBtnVerifyBtnLabel,
    backgroundColor: theme.verifyAccountItemBtnVerifyBtnBG,
    borderColor: theme.verifyAccountItemBtnVerifyBtnBorder
  };

  const textInputBG = {
    color: theme.verifyAccountItemInputText,
    backgroundColor: theme.verifyAccountItemInputBG
  };

  const enterCode = {
    color: theme.verifyAccountItemEnterCode
  };

  const inputSx: SxProps<Theme> = {
    '& .MuiInputBase-input': {
      color: theme.verifyAccountItemInputText,
      fontSize: '1.1rem',
      fontFamily: 'ProximaNova-Regular',
      textTransform: 'none'
    },
    '& .MuiFormHelperText-root.Mui-error': {
      backgroundColor: theme.verifyAccountItemContent,
      margin: 0,
      color: theme.verifyAccountItemErrorText,
      paddingLeft: '5px',
      fontSize: '1.1rem',
      fontFamily: 'ProximaNova-Regular',
      textTransform: 'none'
    },
    '& .MuiInputLabel-root': {
      color: theme.verifyAccountItemInputText,
      fontSize: '1.1rem',
      fontFamily: 'ProximaNova-Regular',
      textTransform: 'none'
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.verifyAccountItemInputText,
      fontSize: '1.1rem',
      fontFamily: 'ProximaNova-Regular',
      textTransform: 'none'
    },
    '& input::placeholder': {
      color: theme.verifyAccountItemInputPlaceHolder,
      fontSize: '1.1rem',
      textTransform: 'none'
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.verifyAccountItemInputText
      },
      '&:hover fieldset': {
        borderColor: theme.verifyAccountItemInputText
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.verifyAccountItemInputText,
        borderWidth: '1px'
      }
    }
  };

  const onClickVerifyBtn = () => {
    if (!expand) onPressSendOTP();
    setExpand(!expand);
  };

  return (
    <Card className={'verify-phone-item-container'} style={container}>
      <View className={'verify-phone-item-header-container'} style={headerContainer}>
        <View className={'verify-phone-item-logo-title-wrapper'}>
          <Constants.Svgs.Mobile
            fillPrimary={theme.svgPrimary}
            fillSecondary={theme.svgSecondary}
          />
          <Text className={'verify-phone-item-title'} style={headerTitle}>{`Phone`}</Text>
        </View>

        {verified && (
          <View className={'verify-phone-item-logo-title-wrapper'}>
            <Constants.Svgs.VerificationCheck
              width={25}
              height={25}
              fillPrimary={theme.profileIndicatorCheckedPrimary}
              fillSecondary={theme.profileIndicatorCheckedSecondary}
            />
            <Text className={'verify-phone-item-verified'} style={headerVerified}>
              {'Verified'}
            </Text>
          </View>
        )}

        {isNeedToVerify && (
          <Button
            style={verifyBtn}
            onClick={onClickVerifyBtn}
            className="verify-phone-item-verify-button">
            {'Verify'}
          </Button>
        )}
      </View>
      {item?.phone && !expand && <PhoneDetails item={item} />}

      {expand && (
        <View className={'verify-phone-item-content-wrapper'} style={contentWrapper}>
          <TextField
            style={{ ...textInputBG }}
            value={code}
            placeholder={'Enter The Code'}
            className={'verify-phone-item-text-input'}
            label={'Enter The Code'}
            variant="outlined"
            size="small"
            onChange={handleChange}
            sx={inputSx}
            inputProps={{ maxLength: 6 }}
          />
          {isNeedToVerify && (
            <Text className={'verify-phone-item-enter-code'} style={enterCode}>
              {'Enter the Code Sent to your mobile number'}
            </Text>
          )}
          <Button style={verifyBtn} onClick={onPressVerify} className={'verify-phone-item-btn'}>
            {'Verify'}
          </Button>

          <Text
            className={'verify-phone-item-enter-code'}
            onPress={onPressResendOTP}
            style={{ ...enterCode, textDecorationLine: 'underline' }}>
            {'Resend'}
          </Text>
        </View>
      )}
    </Card>
  );
};
export default React.memo(VerifyPhoneItem);
